'use client'

import { FC, useEffect, useState } from 'react'
import { DEFAULT_POPUP_CTA_TEXT } from '@/components/scenario/popupCta'
import { Play } from 'lucide-react'

export interface FloatingVideoThumbnailProps {
  thumbnailUrl: string
  thumbnailMimeType: string
  onClick: () => void
  /**
   * ホバー時にサムネイルの上へスライド表示する CTA テキスト。
   * 管理画面（scenario.config.popupCtaText）から流し込む想定。未指定時は既定文言。
   */
  ctaText?: string
}

const FloatingVideoThumbnail: FC<FloatingVideoThumbnailProps> = ({
  thumbnailUrl,
  thumbnailMimeType,
  onClick,
  ctaText = DEFAULT_POPUP_CTA_TEXT,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  // 初期表示は 3 秒間カードを開いておき、その後は通常表示（円形サムネイル）に戻す
  const [initialOpen, setInitialOpen] = useState(true)
  const isVideo = thumbnailMimeType.startsWith('video/')

  useEffect(() => {
    const timer = setTimeout(() => setInitialOpen(false), 3000)
    return () => clearTimeout(timer)
  }, [])

  const isCardOpen = initialOpen || isHovered

  const media = isVideo ? (
    <video src={thumbnailUrl} autoPlay loop muted playsInline className="w-full h-full object-cover" />
  ) : (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={thumbnailUrl} alt="サムネイル" className="w-full h-full object-cover" />
  )

  // 位置は埋め込み先サイトの CSS 変数で上書き可能。既定値は bottom 32px / right 24px。
  // 例: 顧客サイトに固定グローバルメニューがあり重なる場合、顧客側で
  //   @media (max-width: 768px) { :root { --moovy-popup-bottom: 80px; } }
  // を指定すると、CSS 変数が Shadow DOM 境界を継承で貫通して適用される。
  const positionClassName =
    'fixed bottom-[var(--moovy-popup-bottom,32px)] right-[var(--moovy-popup-right,24px)] z-[9999] cursor-pointer group pointer-events-auto'

  return (
    <div
      className={positionClassName}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/*
        通常時の円形サムネイル（96×96）と、展開カード（テキスト＋横長サムネイル）は
        同じ要素のまま形を変える。円 → 角丸の横長へ「ぬるっと」変形させるため、
        crossfade ではなく幅・高さ・角丸・余白・背景をすべて transition している。
        高さは auto のままだが、子（テキストと動画）の高さが毎フレーム変化するので追従する。
        右下を固定したいので bottom-0 right-0 に絶対配置し、左上方向へ広がる。

        サイズ・余白・角丸を px の任意値で指定しているのは、意図した実寸をそのまま
        読めるようにするため。埋め込み CSS はビルド時に rem → px へ一括変換される
        （postcss/rem-to-px.js）ので、rem ベースの既定スケール（w-24 等）を使っても
        埋め込み先の `html { font-size: 62.5% }` の影響は受けない。px 固定は必須ではない。
      */}
      <div
        className={`absolute bottom-0 right-0 z-10 flex flex-col items-center overflow-hidden shadow-2xl transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isCardOpen
            ? 'w-[200px] rounded-[20px] bg-white/75 p-[8px] pb-[12px] ring-1 ring-black/5 backdrop-blur-md'
            : 'w-[96px] rounded-[48px] bg-white/0 p-0 ring-4 ring-white/30 group-hover:ring-white/50'
        }`}
      >
        {/* 文字数超過時は3行で省略（レイアウト崩れ防止の安全ネット）。管理画面側で全角40文字まで。 */}
        <span
          className={`w-full overflow-hidden px-[4px] text-center text-[12px] font-bold leading-relaxed text-gray-900 line-clamp-3 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            isCardOpen ? 'max-h-[80px] pb-[6px] pt-[4px] opacity-100' : 'max-h-0 py-0 opacity-0'
          }`}
        >
          {ctaText}
        </span>

        {/* サムネイル本体（円 96×96 → 横長 184×104 へ変形） */}
        <div
          className={`relative shrink-0 overflow-hidden bg-gray-100 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            isCardOpen ? 'h-[104px] w-[184px] rounded-[14px]' : 'h-[96px] w-[96px] rounded-[48px]'
          }`}
        >
          {media}

          {/* 再生アイコンは円形（通常時）のみ表示する。
              直径96pxの円では共通の再生マーク（72px・ぼかし＋暗幕）が中身をほぼ隠すため、
              ポップアップ型だけは暗幕もぼかしも使わず、影付きの小さな三角だけを置く。 */}
          <div
            className={`absolute inset-0 flex items-center justify-center transition-opacity duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              isCardOpen ? 'opacity-0' : 'opacity-100'
            }`}
          >
            <Play
              aria-hidden="true"
              strokeWidth={2.25}
              className="h-[22px] w-[22px] translate-x-[1px] fill-white text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)] transition-transform duration-200 group-hover:scale-[1.2]"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default FloatingVideoThumbnail
